import {
  Ticket,
  TicketClassification,
  TicketDailyCount,
  TicketReport,
  TicketReportParams,
  TicketSummary,
  TicketVehicleRankingRow,
} from "@/interfaces";
import api from "@/lib/axios";
import { DefaultGetRequestParams, DefaultResponse } from "./types";

interface TicketsResponse extends DefaultResponse<Ticket, true> {}
interface TicketSingleResponse extends DefaultResponse<Ticket, false> {}

export const ticketsService = {
  async index({
    page,
    per_page,
    search,
    kind,
    filters = {},
  }: DefaultGetRequestParams) {
    const response = await api.get<TicketsResponse>("/vehicle_ticket", {
      params: {
        page,
        per_page,
        search,
        kind,
        ...filters,
      },
    });
    return response.data;
  },

  async show(id: number) {
    const response = await api.get<TicketSingleResponse>(
      `/vehicle_ticket/${id}`,
    );
    return response.data;
  },

  async store(data: Partial<Ticket>) {
    const response = await api.post<TicketSingleResponse>(
      "/vehicle_ticket",
      data,
    );
    return response.data;
  },

  async update(id: number | string, data: Partial<Ticket>) {
    const response = await api.put<TicketSingleResponse>(
      `/vehicle_ticket/${id}`,
      data,
    );
    return response.data;
  },

  async updateDriver(id: number, driverId: number | null) {
    const response = await api.put<TicketSingleResponse>(
      `/vehicle_ticket/${id}/driver`,
      { driver_id: driverId },
    );
    return response.data;
  },

  async destroy(id: number) {
    const response = await api.delete<DefaultResponse<never>>(
      `/vehicle_ticket/${id}`,
    );
    return response.data;
  },

  /** The tickets among `ids` that are still pending (IndexRequest caps `per_page` at 200). */
  async pendingTickets(ids: number[]) {
    const response = await api.get<TicketsResponse>("/vehicle_ticket", {
      params: {
        per_page: 200,
        "eq[paid]": "0",
        "in[id]": ids,
      },
    });
    return response.data;
  },

  async vehicleRanking(params: {
    paid?: "0" | "1";
    classification?: TicketClassification[];
    due?: "overdue" | "next_30_days";
  }) {
    const response = await api.get<
      DefaultResponse<TicketVehicleRankingRow[], false>
    >("/vehicle_ticket/vehicle_ranking", { params });
    return response.data;
  },

  async dailyCount(month: string) {
    const response = await api.get<DefaultResponse<TicketDailyCount, false>>(
      "/vehicle_ticket/daily_count",
      { params: { month } },
    );
    return response.data;
  },

  async report(params: TicketReportParams) {
    const response = await api.get<DefaultResponse<TicketReport, false>>(
      "/vehicle_ticket/report",
      { params },
    );
    return response.data;
  },

  /** Downloads the report as CSV or PDF as a blob (keeps the auth header). */
  async reportExport(params: TicketReportParams, format: "csv" | "pdf") {
    const response = await api.get("/vehicle_ticket/report/export", {
      params: { ...params, format },
      responseType: "blob",
    });
    return response.data as Blob;
  },

  async pay(
    paymentDate: string,
    payments: { id: number; payment_amount: number }[],
  ) {
    const response = await api.post<DefaultResponse<never>>(
      "/vehicle_ticket/pay",
      {
        payment_date: paymentDate,
        payments,
      },
    );
    return response.data;
  },

  async undoPayment(id: number) {
    const response = await api.delete<DefaultResponse<never>>(
      `/vehicle_ticket/${id}/pay`,
    );
    return response.data;
  },

  async summary() {
    const response = await api.get<TicketSummary>("/vehicle_ticket/summary");
    return response.data;
  },

  async boleto(id: number) {
    const response = await api.get<{
      download_url: string;
      expires_at: string;
    }>(`/vehicle_ticket/${id}/boleto`);
    return response.data;
  },
};
